/*
 * The drawer loader, put up and taken down here rather than by updateSidebarCart, so the
 * whole removal is driven from one file. Same markup and classes the theme uses, so it
 * picks up the existing .is-loading > .loading-overlay styling.
 */
const DRAWER_WRAPPER = "#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper";

function showDrawerLoader() {
    const wrapper = document.querySelector(DRAWER_WRAPPER);

    if (!wrapper || wrapper.querySelector(".loading-overlay--custom")) return null;

    wrapper.classList.add("is-loading");
    wrapper.insertAdjacentHTML(
        "afterbegin",
        '<div class="loading-overlay loading-overlay--custom">' +
            '<div class="loading-overlay__spinner">' +
                '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
                    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
                '</svg>' +
            '</div>' +
        '</div>'
    );

    return wrapper;
}

function hideDrawerLoader(wrapper) {
    if (!wrapper) return;

    wrapper.classList.remove("is-loading");
    wrapper.querySelector(".loading-overlay--custom")?.remove();
}

/*
 * Removing a line, in two requests and one visible update.
 *
 * It was five requests: GET /cart.js, a POST /cart/change.js per line, another
 * GET /cart.js, then GET /cart?view=ajax_side_cart to rebuild the drawer. That last render
 * is the expensive one - about two seconds - and it was what the customer waited on.
 *
 * So it is no longer waited on. The two changes go in one /cart/update.js, whose response
 * is the whole cart, and the drawer is updated from that: the line, the count, the money
 * and the savings row all move together, in the same frame. The loader covers only the
 * request, not a render behind it.
 *
 * The full re-render is kept for the one case the markup here cannot produce - an empty
 * cart, which has its own layout - and for anything else the drawer holds that only Liquid
 * knows, the free-shipping message is nudged through the theme's own handler instead.
 *
 * The opening GET /cart.js stays: it is what identifies the linked Coriguard line. Reading
 * that from the drawer markup would save a trip, but a wrong guess orphans a warranty line
 * and the customer keeps paying for cover on a couch they removed.
 */
async function updateProductQuantities(mainProductId, mainProductIdKey, newQuantity) {
    const loader = showDrawerLoader();

    try {
        const cart = await fetch("/cart.js").then(res => res.json());

        let mainProduct = null;
        let linkedProduct = null;

        cart.items.forEach(item => {
            if (item.key == mainProductIdKey) {
                mainProduct = item;
            }
            if (item.properties && item.properties.CoriguardProduct == mainProductId) {
                linkedProduct = item;
            }
        });

        if (!mainProduct) {
            hideDrawerLoader(loader);
            return;
        }

        const updates = {};
        updates[mainProduct.key] = newQuantity;

        if (linkedProduct) {
            updates[linkedProduct.key] = newQuantity;
        }

        const response = await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates })
        });

        if (!response.ok) {
            throw new Error(`Failed to update cart: ${response.statusText}`);
        }

        const updatedCart = await response.json();

        if (updatedCart.item_count === 0) {
            // The empty drawer is a different layout, so let Liquid build that one.
            window.sharedFunctions.updateSidebarCart(updatedCart);
            return;
        }

        applyCartToDrawer(updates, updatedCart);
        hideDrawerLoader(loader);

        /*
         * The same nudge halo.dispatchChangeForShippingMessage does. Dispatched here rather
         * than called through window.sharedFunctions, which does not expose that method -
         * only updateSidebarCart and a handful of others are on it - so calling it would
         * have silently done nothing and the free-shipping bar would have kept the old
         * total.
         */
        document.querySelectorAll("[data-free-shipping-wrapper]").forEach((wrapper) => {
            wrapper.dispatchEvent(new Event("change", { bubbles: true }));
        });

        document.dispatchEvent(new CustomEvent("cart-update", { detail: updatedCart }));
    } catch (error) {
        console.error("Error updating cart:", error);
        hideDrawerLoader(loader);

        // Nothing was changed on screen before the request came back, so there is nothing
        // to undo - but the drawer may now disagree with the cart, so let Liquid settle it.
        window.sharedFunctions.updateSidebarCart?.(await fetch("/cart.js").then(res => res.json()));
    }
}

/*
 * Defensive on purpose: every element is optional and a missing one is skipped rather than
 * thrown on, because the silent re-render right behind this is the authority. This only
 * exists so the change is not held back waiting for it.
 */
function applyCartToDrawer(updates, cart) {
    try {
        Object.keys(updates).forEach((key) => {
            if (updates[key] !== 0) return;

            document
                .querySelector(`[data-cart-remove][data-line="${key}"]`)
                ?.closest(".previewCartItem")
                ?.remove();
        });

        const money = (cents) =>
            typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function"
                ? Shopify.formatMoney(cents, window.money_format)
                : null;

        /*
         * Each line's own price, scaled to its quantity.
         *
         * The drawer renders item.final_price and item.original_price, which are per unit, so
         * a line sat at R 57,499 while the subtotal beside it read R 114,998 for the same two
         * items. The cart carries the multiplied figures already - final_line_price and
         * original_line_price - so they are taken from there rather than multiplied here.
         *
         * The compare-at branch is the exception: /cart.js has no compare_at_price, so that
         * one unit value is published on the wrapper by snippets/halo-side-cart.liquid and
         * multiplied by the quantity the same cart reports.
         */
        (cart.items || []).forEach((item) => {
            const row = document
                .querySelector(`[data-cart-remove][data-line="${item.key}"]`)
                ?.closest(".previewCartItem");

            if (!row) return;

            /*
             * The quantity the row shows, which is not always an input.
             *
             * A Coriguard cover has no stepper - snippets/halo-side-cart.liquid prints
             * "Quantity: n" as text for it - and nothing here was writing that. So raising the
             * couch to 12 moved both lines in the cart and both prices on screen, while the
             * cover went on reading "Quantity: 11" beside a price that had already changed.
             */
            const quantityInput = row.querySelector(".previewCartItem-qty-input");

            if (quantityInput && String(quantityInput.value) !== String(item.quantity)) {
                quantityInput.value = item.quantity;
            }

            // Misspelled in the theme's markup; matched as it is written there.
            const quantityText = row.querySelector(".corigruad-quanity");

            if (quantityText) quantityText.textContent = item.quantity;

            const wrapper = row.querySelector(".previewCartItem-price");

            if (!wrapper) return;

            const finalEl = wrapper.querySelector("[data-item-final-price-display]");
            const finalPrice = money(item.final_line_price);

            if (finalEl && finalPrice) finalEl.textContent = finalPrice;

            const originalEl = wrapper.querySelector("[data-item-original-price-display]");

            if (!originalEl) return;

            const compare = wrapper.querySelector("[data-price-from-compare]");
            const compareUnit = compare ? parseInt(compare.getAttribute("data-compare-price"), 10) : NaN;
            const originalPrice = isNaN(compareUnit)
                ? money(item.original_line_price)
                : money(compareUnit * item.quantity);

            if (originalPrice) originalEl.textContent = originalPrice;
        });

        const subtotal = money(cart.original_total_price);
        const total = money(cart.total_price);

        if (subtotal) {
            document.querySelectorAll("[data-cart-subtotal]").forEach((el) => {
                el.innerHTML = subtotal;
            });
        }

        if (total) {
            document.querySelectorAll("[data-cart-total-value]").forEach((el) => {
                el.setAttribute("data-cart-total-value", cart.total_price);
                el.innerHTML = total;
            });
        }

        document.querySelectorAll("[data-saved-discount-display]").forEach((el) => {
            el.style.display = cart.total_discount === 0 ? "none" : "";
        });

        document.querySelectorAll("[data-cart-count]").forEach((el) => {
            el.textContent =
                cart.item_count >= 100 && window.cartStrings
                    ? window.cartStrings.item_99
                    : cart.item_count;
        });

        if (window.cartStrings) {
            document.querySelectorAll("[data-cart-text]").forEach((el) => {
                el.textContent = cart.item_count === 1 ? window.cartStrings.item : window.cartStrings.items;
            });
        }
    } catch (error) {
        console.error("Error applying cart removal:", error);
    }
}

/*
 * Published deliberately, rather than left as an implicit global: theme.js's plain quantity
 * handler reads it to move the totals off the cart its own write returned, instead of
 * waiting on the drawer re-render. It guards on the function being there, so this file not
 * loading only costs that shortcut.
 */
window.applyCartToDrawer = applyCartToDrawer;

async function removeProductProperty(mainProductId, mainProductIdKey) {
    try {
        const cart = await fetch("/cart.js").then(res => res.json());
        let linkedProduct = null;

        cart.items.forEach(item => {
            if (item.properties && item.properties.CoriguardProduct == mainProductId) {
                linkedProduct = item;
            }
        });

        if (linkedProduct) {
            await fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: linkedProduct.key, quantity: 0 })
            });

            await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: linkedProduct.id, quantity: linkedProduct.quantity, properties: {} })
            });
        }
        fetchCartData();
    } catch (error) {
        console.error("Error updating cart:", error);
    }
}

async function updateCartItem(cartItemKey, quantity) {
    return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: cartItemKey, quantity })
    }).then(res => res.json());
}

async function removeCartItem(cartItemKey) {
    try {
        const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: cartItemKey, quantity: 0 })
        });

        if (!response.ok) {
            throw new Error(`Failed to remove item from cart: ${response.statusText}`);
        }

        const removeButton = document.querySelector(`[data-cart-remove-id="${cartItemKey}"]`);
        removeButton?.closest(".previewCartItem")?.remove();
    } catch (error) {
        console.error("Error removing item from cart:", error);
    }
}

// async function updateCartSidebar() {
//     try {
//         const cart = await fetch("/cart.js").then(res => res.json());
//         window.sharedFunctions.updateSidebarCart(cart);
//     } catch (error) {
//         console.error("Error fetching cart data:", error);
//     }
// }

function debounce(func, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

async function fetchCartData() {
    try {
        const cart = await fetch("/cart.js").then(res => res.json());
        window.sharedFunctions.updateSidebarCart(cart);
    } catch (error) {
        console.error("Error fetching cart data:", error);
    }
}
// const updateCartSidebar = debounce(fetchCartData, 300);

if (typeof updateCartSidebar === 'undefined') {
  const updateCartSidebar = debounce(fetchCartData, 300);
}


// These three are DELEGATED listeners on `document`, so they only ever need to be
// registered once. They used to be registered again on every drawer render, because
// snippets/halo-side-cart.liquid loaded this file from inside the AJAX drawer body and
// jQuery .html() re-executes injected scripts. Two drawer opens meant two listeners, so a
// single remove click fired two cart updates and two drawer re-renders — the "loader runs
// 2-3 times" bug.
//
// halo-side-cart.liquid now loads this file once, out of band. This guard is the second
// line of defence: even if this file is evaluated again, the listeners are not duplicated.
if (!window.__coriguardSideCartWired) {
    window.__coriguardSideCartWired = true;

    document.addEventListener("change", function (event) {
        if (event.target.matches(".previewCartItem-qty-input")) {
            let checkForCorigraud = event.target.getAttribute("data-corigraud-product-id");
            if (checkForCorigraud) {
                let mainProductId = event.target.getAttribute("data-cart-quantity-id");
                let newQuantity = parseInt(event.target.value);
                let mainProductIdKey = event.target.getAttribute("data-line");
                updateProductQuantities(mainProductId, mainProductIdKey, newQuantity);

            }
        }
    });

    document.addEventListener("click", function (event) {
        if (event.target.closest(".previewCartItem-remove")) {
            let removeButton = event.target.closest(".previewCartItem-remove");
            let mainProductId = removeButton.getAttribute("data-cart-remove-id");
            let cartLine = removeButton.getAttribute("data-line");
            updateProductQuantities(mainProductId, cartLine, 0);

        }
    });

    document.addEventListener("click", async function (event) {
        const removeButton = event.target.closest(".corigraud-product-remove");
        if (removeButton) {
            event.preventDefault();
            let mainProductId = removeButton.getAttribute("data-cart-remove-id");
            let mainProductIdKey = removeButton.getAttribute("data-line");
            await removeProductProperty(mainProductId, mainProductIdKey);

        }
    });
}
